import { RUNTIME_EVENTS, graphSchema } from './graph.js';
/** A v4 uuid, for rows this file makes at run time rather than at author time. */
function uuid() {
    return globalThis.crypto.randomUUID();
}
/**
 * How every graph opens: the entry edge comes from nowhere into a Start node,
 * and Start leads on to the first node that speaks.
 *
 * Templates build their rows by hand, so without this each one writes the same
 * opening again. One helper keeps them in step: a change to how a graph opens
 * is a change here, not in eleven places.
 */
export function startConversationOpening(options) {
    return {
        node: {
            id: options.startNodeId,
            type: 'start_conversation',
            title: 'Start conversation',
            position: 0,
            // Empty until the author picks: a template does not know which trigger
            // the graph made from it will answer, so it knows neither the channel
            // nor the variable holding where to send.
            channels: [],
            destinationVariable: null,
        },
        edges: [
            {
                id: options.entryEdgeId,
                fromNodeId: null,
                toNodeId: options.startNodeId,
                condition: {
                    type: 'event',
                    event: RUNTIME_EVENTS.SESSION_STARTED,
                },
                position: 0,
            },
            {
                id: options.exitEdgeId,
                fromNodeId: options.startNodeId,
                toNodeId: options.toNodeId,
                condition: {
                    type: 'event',
                    event: RUNTIME_EVENTS.CONVERSATION_STARTED,
                },
                position: 1,
            },
        ],
    };
}
/**
 * A graph saved before Start was a node, given the opening it now needs.
 *
 * Such a graph points its entry edge straight at the first node that speaks,
 * which the contract no longer allows. Rather than refuse it — the caller only
 * learns that the graph is invalid, never why — give it the Start node it is
 * missing. The same move the database backfill makes, for a graph that arrives
 * as a payload instead: a shared link, or a graph carried on a dispatch.
 *
 * Takes what the caller was handed, so a door that holds an unchecked payload
 * passes it straight through. Anything that is not a graph is returned as it
 * came, for the parse after this one to refuse.
 */
export function withStartConversation(graph) {
    const candidate = graphSchema.safeParse(graph);
    if (!candidate.success)
        return graph;
    return migrateParsed(candidate.data);
}
function migrateParsed(graph) {
    if (graph.nodes.some(node => node.type === 'start_conversation')) {
        return graph;
    }
    const entry = graph.edges.find(edge => edge.fromNodeId == null &&
        edge.condition.type === 'event' &&
        edge.condition.event === RUNTIME_EVENTS.SESSION_STARTED);
    // No entry edge, or one that ends the call, leaves nothing to put Start in
    // front of. Hand it back and let validation say what is wrong with it.
    if (!entry?.toNodeId)
        return graph;
    const opening = startConversationOpening({
        startNodeId: uuid(),
        entryEdgeId: entry.id,
        exitEdgeId: uuid(),
        toNodeId: entry.toNodeId,
    });
    const [entryEdge, exitEdge] = opening.edges;
    // The Start pill used to sit on the entry edge as `sourceNodePos`. Start is
    // a node now, so give the position to the node and drop the key. Without
    // this the node opens at the origin, on top of the entry anchor, and the
    // stale key stays in the graph. The database backfill makes the same move.
    const { sourceNodePos, ...entryLayout } = entry.layoutMetadata ?? {};
    const startNode = {
        ...opening.node,
        position: graph.nodes.length,
        ...(sourceNodePos && { layoutMetadata: { position: sourceNodePos } }),
    };
    return {
        ...graph,
        nodes: [...graph.nodes, startNode],
        edges: [
            ...graph.edges.map(edge => edge.id === entry.id
                ? {
                    ...edge,
                    ...entryEdge,
                    ...(Object.keys(entryLayout).length > 0
                        ? { layoutMetadata: entryLayout }
                        : { layoutMetadata: undefined }),
                }
                : edge),
            { ...exitEdge, position: graph.edges.length },
        ],
    };
}
//# sourceMappingURL=template-openings.js.map